/* eslint-disable max-len */
import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../../common/Modal'
import { ModalHeader } from '../../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { ToastType } from '../../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { societyDocumentUpdateSchema } from '../../../../../forms/schemas/society_document_update'
import { societyDocumentUploadSchema } from '../../../../../forms/schemas/society_document_upload'
import { UpdateSocietyWebsiteDocumentForm } from '../CreateUpdateSocietyWebsiteDocumentForm/UpdateSocietyWebsiteDocumentForm'
import { CreateSocietyWebsiteDocumentForm } from '../CreateUpdateSocietyWebsiteDocumentForm/CreateSocietyWebsiteDocumentForm'
import { ISocietyWebsiteWithFiles } from '../../../../../interfaces/services/societies-websites.interfaces'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import { createDocument, patchDocument } from '../../../../../api/documents'
import { IDocumentModel } from '../../../../../interfaces/models/documents.interfaces'
import { patchWebsite } from '../../../../../api/society-websites'
import { DocumentView } from '../../../../../generated/api/file-view'

interface CreateUpdateSocietyWebsiteDocumentModalProps {
  website: ISocietyWebsiteWithFiles
  society: ISocietiesModel
  show: boolean
  close: () => void
  folderId: string
  children?: React.ReactNode
  document?: DocumentView
}

export const CreateUpdateSocietyWebsiteDocumentModal = ({
  website,
  document,
  society,
  folderId,
  children,
  show,
  close,
}: CreateUpdateSocietyWebsiteDocumentModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!document

  const folders = website ? website.foldersList : []

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateDocument.flashMessage.updateDocumentFailure'
      : 'createUpdateDocument.flashMessage.createDocumentFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateDocument.flashMessage.updateDocumentSuccess'
      : 'createUpdateDocument.flashMessage.createDocumentSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchDocument, isPending: isPatchingDocuments } =
    useMutation({
      mutationFn: patchDocument,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmitUpdateDocument = async (
    data: InferType<typeof societyDocumentUpdateSchema>
  ): Promise<void> => {
    document && mutatePatchDocument({ id: document.id, body: data })
  }

  const showMessage = (type: ToastType, message: string): void => {
    setToastNotification(type, message)
  }

  const getSuccessMessage = (isSingular: boolean): string => {
    return translate(
      isSingular
        ? 'createUpdateDocument.flashMessage.createDocumentSingularSuccess'
        : 'createUpdateDocument.flashMessage.createDocumentPluralSuccess'
    )
  }

  const getFailureMessage = (isSingular: boolean): string => {
    return translate(
      isSingular
        ? 'createUpdateDocument.flashMessage.createDocumentSingularFailure'
        : 'createUpdateDocument.flashMessage.createDocumentPluralFailure'
    )
  }

  const { mutate: mutateCreateDocument, isPending: isCreatingDocuments } =
    useMutation({
      mutationFn: async (
        data: InferType<typeof societyDocumentUploadSchema>
      ) => {
        const ids: string[] = []
        const promises = data.files.map(async (_file) => {
          const response = await createDocument({
            data: _file.base64,
            name: _file.name,
            filename: _file.filename,
            type: _file.filename.split('.').pop() as IDocumentModel['type'],
            parentId: folderId,
            societyId: society._id,
            section: 'society-website',
            notificationSettings: 'none',
            public: true,
          })
          if (response.data.data) {
            ids.push(response.data.data._id)
          }
        })
        await Promise.all(promises)
        if (!ids) {
          throw new Error()
        }
        await patchWebsite({
          id: website._id,
          body: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            foldersList: folders?.map((_f: any) => {
              if (_f._id === folderId) {
                return {
                  ..._f,
                  documentsList: [
                    ..._f.documentsList,
                    ...ids.map((_id) => ({ documentId: _id })),
                  ],
                }
              }
              return _f
            }),
          },
        })
      },
      onSuccess: (_data, input) => {
        const isOneFile = input.files.length === 1
        showMessage(ToastType.SUCCESS, getSuccessMessage(isOneFile))
        close()
      },
      onError: (_error, input) => {
        const isOneFile = input.files.length === 1
        showMessage(ToastType.DANGER, getFailureMessage(isOneFile))
      },
    })
  const onSubmitCreateDocument = async (
    data: InferType<typeof societyDocumentUploadSchema>
  ): Promise<void> => {
    mutateCreateDocument(data)
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isPatchingDocuments || isCreatingDocuments

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateDocument.editDocumentTitle'
                  : 'createUpdateDocument.addNewDocumentTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {updateMode && document ? (
                <UpdateSocietyWebsiteDocumentForm
                  onError={onError}
                  onSubmit={onSubmitUpdateDocument}
                  onClose={close}
                  loading={loading}
                  document={document}
                />
              ) : (
                <CreateSocietyWebsiteDocumentForm
                  onError={onError}
                  onSubmit={onSubmitCreateDocument}
                  onClose={close}
                  loading={loading}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
